import React, {useState} from 'react';

import Dropdown from 'ui-library/dropdowns/Dropdown';
import TextInput from 'ui-library/inputs/TextInput';
import Menu from 'ui-library/menus/Menu';
import {MenuHeight, MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';

const NO_CHANGE_TEXT = 'Keep existing types';

export interface AnnotationTypeDropdownInputProps {
  className?: string;
  type: string | null;
  allTypes: Array<string>;
  onChangeType: (type: string | null) => void;
  onBlur?: () => void;
  includeNullOption?: boolean;
  disabled?: boolean;
}

/**
 * A text input component for annotation types that can open a dropdown of type suggestions to select from
 */
export default function AnnotationTypeDropdownInput({
  className,
  type,
  allTypes,
  onChangeType,
  onBlur,
  includeNullOption = false,
  disabled = false,
}: AnnotationTypeDropdownInputProps): JSX.Element {
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const filteredTypes = allTypes.filter((t) => t.includes(type ?? ''));
  const typeOptions = includeNullOption ? [...filteredTypes, NO_CHANGE_TEXT] : filteredTypes;

  return (
    <Dropdown
      alwaysDropDown={true}
      isOpen={isDropdownOpen && typeOptions.length > 0}
      menu={
        <Menu
          value={type || NO_CHANGE_TEXT}
          height={MenuHeight.MEDIUM}
          width={MenuWidth.FULL}
          scrollable={true}
        >
          {typeOptions.map((type) => (
            <Option
              key={type}
              value={type}
              onSelect={(newType: string) => {
                if (newType === NO_CHANGE_TEXT) {
                  onChangeType(null);
                } else {
                  onChangeType(newType);
                }
              }}
            >
              {type}
            </Option>
          ))}
        </Menu>
      }
      onToggle={setIsDropdownOpen}
    >
      <TextInput
        className={className}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          onChangeType(e.target.value);
        }}
        onFocus={() => {
          if (!disabled) {
            setIsDropdownOpen(true);
          }
        }}
        onBlur={() => {
          setIsDropdownOpen(false);
          onBlur?.();
        }}
        placeholder="Annotation type"
        value={includeNullOption && type == null && !isDropdownOpen ? NO_CHANGE_TEXT : (type ?? '')}
        disabled={disabled}
      />
    </Dropdown>
  );
}
